import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import BlacksmithWorkshop from '../components/BlacksmithWorkshop';
import ConnectWalletModal from '../components/ConnectWalletModal';
import { createICSpicyActor } from '../services/icspicy';
import { useAuth } from '../auth/useAuth';

interface ShopProduct {
  id: string;
  name: string;
  description: string;
  price_usd: number;
  category: { [key: string]: any };
  inventory: number;
  in_stock: boolean;
  image_url?: string;
}

interface CartItem {
  product: ShopProduct;
  quantity: number;
}

export default function ShopPage() {
  const { agent, principal, isAuthenticated } = useAuth();
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [products, setProducts] = useState<ShopProduct[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [checkoutAddress, setCheckoutAddress] = useState('');
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (agent) {
      loadProducts();
    }
  }, [agent, selectedCategory]);

  const loadProducts = async () => {
    if (!agent) return;
    setLoading(true);
    try {
      const actor = await createICSpicyActor(agent);
      const category = selectedCategory ? { [selectedCategory]: null } : null;
      const prods = await actor.get_shop_products(category);
      setProducts(prods);
    } catch (err) {
      console.error('Failed to load products:', err);
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product: ShopProduct) => {
    if (!product.in_stock) {
      setError('Product is out of stock');
      return;
    }
    setCart(prev => {
      const existing = prev.find(item => item.product.id === product.id);
      if (existing) {
        if (existing.quantity >= product.inventory) {
          setError('Cannot add more - inventory limit reached');
          return prev;
        }
        return prev.map(item =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { product, quantity: 1 }];
    });
    setError(null);
  };

  const removeFromCart = (productId: string) => {
    setCart(prev => prev.filter(item => item.product.id !== productId));
  };

  const updateCartQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    const product = products.find(p => p.id === productId);
    if (product && quantity > product.inventory) {
      setError('Quantity exceeds available inventory');
      return;
    }
    setCart(prev =>
      prev.map(item =>
        item.product.id === productId ? { ...item, quantity } : item
      )
    );
    setError(null);
  };

  const getTotal = () => {
    return cart.reduce((sum, item) => sum + item.product.price_usd * item.quantity, 0);
  };

  const handleCheckout = async () => {
    if (!agent || !isAuthenticated || !principal) {
      setShowConnectModal(true);
      return;
    }
    if (!checkoutAddress.trim()) {
      setError('Please enter a shipping address');
      return;
    }
    if (cart.length === 0) {
      setError('Cart is empty');
      return;
    }

    setPlacingOrder(true);
    setError(null);
    try {
      const actor = await createICSpicyActor(agent);
      const items = cart.map(item => [item.product.id, item.quantity] as [string, number]);
      const orderId = await actor.place_order(items, checkoutAddress);
      setSuccess(`Order placed successfully! Order ID: ${orderId}`);
      setCart([]);
      setCheckoutOpen(false);
      setCheckoutAddress('');
      loadProducts(); // Refresh inventory
    } catch (err: any) {
      setError(err.message || 'Failed to place order');
    } finally {
      setPlacingOrder(false);
    }
  };

  const categories = ['Pods', 'Plants', 'Seeds', 'SpiceBlends', 'Other'];

  if (!isAuthenticated) {
    return (
      <BlacksmithWorkshop>
        <div className="container mx-auto px-4 py-8 text-center">
          <h1 className="text-4xl font-bold mb-4 text-gold">IC SPICY Shop</h1>
          <p className="text-gray-300 mb-6">Connect your wallet to shop for fresh peppers and products</p>
          <button
            onClick={() => setShowConnectModal(true)}
            className="px-6 py-3 bg-gold text-black rounded-lg font-semibold hover:bg-yellow-500 transition"
          >
            Connect Wallet
          </button>
          {showConnectModal && (
            <ConnectWalletModal onClose={() => setShowConnectModal(false)} />
          )}
        </div>
      </BlacksmithWorkshop>
    );
  }

  return (
    <BlacksmithWorkshop>
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-gold via-yellow-500 to-gold bg-clip-text text-transparent">
            IC SPICY Shop
          </h1>
          <p className="text-xl text-gray-300">Fresh Florida Peppers & Products</p>
        </motion.div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-6 justify-center">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-4 py-2 rounded-lg ${
              selectedCategory === null
                ? 'bg-gold text-black'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            All
          </button>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-2 rounded-lg ${
                selectedCategory === cat
                  ? 'bg-gold text-black'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-4 p-4 bg-red-900/50 border border-red-500 rounded-lg text-red-200">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-900/50 border border-green-500 rounded-lg text-green-200">
            {success}
          </div>
        )}

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading products...</div>
        ) : products.length === 0 ? (
          <div className="text-center py-12 text-gray-400">No products available</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {products.map(product => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ scale: 1.05 }}
                className="glass-dark rounded-2xl p-6"
              >
                {product.image_url && (
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-48 object-cover rounded-lg mb-4"
                  />
                )}
                <h3 className="text-xl font-bold text-gold mb-2">{product.name}</h3>
                <p className="text-gray-400 text-sm mb-4">{product.description}</p>
                <div className="flex justify-between items-center mb-4">
                  <span className="text-2xl font-bold text-white">
                    ${product.price_usd.toFixed(2)}
                  </span>
                  <span
                    className={`px-3 py-1 rounded ${
                      product.in_stock
                        ? 'bg-green-900/50 text-green-300'
                        : 'bg-red-900/50 text-red-300'
                    }`}
                  >
                    {product.in_stock ? 'In Stock' : 'Out of Stock'}
                  </span>
                </div>
                <div className="text-sm text-gray-400 mb-4">
                  Inventory: {product.inventory}
                </div>
                <button
                  onClick={() => addToCart(product)}
                  disabled={!product.in_stock}
                  className={`w-full py-2 rounded-lg font-semibold transition ${
                    product.in_stock
                      ? 'bg-gold text-black hover:bg-yellow-500'
                      : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Add to Cart
                </button>
              </motion.div>
            ))}
          </div>
        )}

        {/* Cart */}
        {cart.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-dark rounded-2xl p-6 fixed bottom-4 right-4 w-80 z-50"
          >
            <h3 className="text-xl font-bold text-gold mb-4">Cart ({cart.length})</h3>
            <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
              {cart.map(item => (
                <div key={item.product.id} className="flex justify-between items-center">
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-white">{item.product.name}</div>
                    <div className="text-xs text-gray-400">
                      ${item.product.price_usd.toFixed(2)} × {item.quantity}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => updateCartQuantity(item.product.id, item.quantity - 1)}
                      className="px-2 py-1 bg-gray-700 rounded text-sm"
                    >
                      -
                    </button>
                    <span className="text-sm">{item.quantity}</span>
                    <button
                      onClick={() => updateCartQuantity(item.product.id, item.quantity + 1)}
                      className="px-2 py-1 bg-gray-700 rounded text-sm"
                    >
                      +
                    </button>
                    <button
                      onClick={() => removeFromCart(item.product.id)}
                      className="px-2 py-1 bg-red-900/50 rounded text-sm"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-gray-700 pt-4 mb-4">
              <div className="flex justify-between text-lg font-bold text-white mb-4">
                <span>Total:</span>
                <span>${getTotal().toFixed(2)}</span>
              </div>
              <button
                onClick={() => setCheckoutOpen(true)}
                className="w-full py-2 bg-gold text-black rounded-lg font-semibold hover:bg-yellow-500 transition"
              >
                Checkout
              </button>
            </div>
          </motion.div>
        )}

        {/* Checkout Modal */}
        {checkoutOpen && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="glass-dark rounded-2xl p-6 w-full max-w-md"
            >
              <h3 className="text-2xl font-bold text-gold mb-4">Checkout</h3>
              <div className="mb-4">
                <label className="block text-gray-300 mb-2">Shipping Address</label>
                <textarea
                  value={checkoutAddress}
                  onChange={e => setCheckoutAddress(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  rows={3}
                  placeholder="Enter your shipping address"
                />
              </div>
              <div className="mb-4">
                <div className="text-gray-300 mb-2">Order Total: ${getTotal().toFixed(2)}</div>
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() => setCheckoutOpen(false)}
                  className="flex-1 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCheckout}
                  disabled={placingOrder}
                  className="flex-1 py-2 bg-gold text-black rounded-lg font-semibold hover:bg-yellow-500 disabled:opacity-50"
                >
                  {placingOrder ? 'Placing Order...' : 'Place Order'}
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {showConnectModal && (
          <ConnectWalletModal onClose={() => setShowConnectModal(false)} />
        )}
      </div>
    </BlacksmithWorkshop>
  );
}

